import React from 'react';
import { useServerData } from '@xingternal/hops';
import cx from 'classnames';

import { Card } from '@xingternal/card';
import { Headline, BodyCopy, Meta } from '@xingternal/typography';
import { ProfileImage } from '@xingternal/profile-image';
import { Button } from '@xingternal/button';
import { StarRating } from '@xingternal/star-rating';

import { changeURLtoMobile } from '@/routes/routes';

import styles from './CompanyCard.css';
import { FormattedMessage } from 'react-intl';
import freeCover from '@/assets/svg/FreeCover.svg';
import {
  Company,
  EntityPagePublicationStatus,
  Maybe,
  EntityPageContractType,
} from '@/types/graphql-types';

const getDraftText: (
  // eslint-disable-next-line no-unused-vars
  publicationStatus: EntityPagePublicationStatus | undefined,
) => string = (publicationStatus) => {
  if (publicationStatus === 'DRAFT') {
    return 'CP_DRAFT_COMPANY_WAITING_APPROVAL';
  } else if (publicationStatus === 'CREATED') {
    return 'CP_DRAFT_COMPANY_FLAG_WARNING';
  }

  return '';
};

const isPublished: (
  publicationStatus: EntityPagePublicationStatus | undefined,
) => boolean = (publicationStatus) =>
  !publicationStatus ||
  (publicationStatus && publicationStatus === 'PUBLISHED');

type CompanyCardProps = {
  company: Maybe<Company>;
  buttons?: Array<Maybe<JSX.Element>>;
  customContent?: JSX.Element;
  className?: string;
};
const CompanyCard: React.FC<CompanyCardProps> = ({
  company,
  className,
  buttons,
  customContent,
}) => {
  const { authenticated, mobileProtocol, android } = useServerData();

  const isFreeCompany =
    company?.entityPage?.contract?.type === EntityPageContractType.Free;

  const publicationStatus = company?.entityPage?.publicationStatus;
  const showButtons =
    buttons &&
    buttons.length > 0 &&
    isPublished(company?.entityPage?.publicationStatus);
  const companyURL = company?.links?.public || company?.links?.default;
  const coverImage = !isFreeCompany
    ? company?.entityPage?.coverImage?.[0]?.url
    : undefined;
  const urlWithReleaseProtocol = mobileProtocol
    ? changeURLtoMobile({
        mobileProtocol,
        url: companyURL,
        slug: company?.entityPage?.slug,
        android,
      })
    : companyURL;

  return (
    <>
      <Card
        data-testid={`company-card-${company?.id}`}
        className={cx(className, styles.card)}
      >
        {publicationStatus && publicationStatus !== 'PUBLISHED' ? (
          <div className={cx(className, styles.overlayLayer, styles.card)}>
            <div className={styles.flag}>
              <FormattedMessage id={getDraftText(publicationStatus)} />
            </div>
          </div>
        ) : null}
        <a
          href={urlWithReleaseProtocol}
          data-testid="link-to-profile"
          className={styles.profileLink}
        >
          <div
            className={styles.coverImage}
            data-testid={`cover-image-card`}
            style={
              coverImage
                ? { backgroundImage: `url(${coverImage}) ` }
                : { backgroundImage: `url(${freeCover}) ` }
            }
          >
            <ProfileImage
              size="medium"
              profileName={company?.companyName}
              src={company?.logos?.logo128px}
              type="company"
              className={styles.logo}
            />
          </div>
          <Headline as="strong" noMargin className={styles.companyName}>
            {company?.companyName}
          </Headline>
        </a>

        {company?.address?.city ? (
          <BodyCopy data-testid="company-city" noMargin size="medium">
            {company?.address?.city}
          </BodyCopy>
        ) : null}
        {company?.industry?.localizationValue ? (
          <Meta data-testid="company-industry" size="small">
            {company?.industry?.localizationValue}
          </Meta>
        ) : null}
        {company?.kununuData && (
          <div
            data-testid="kununu-section"
            className={cx(styles.kununuRating, {
              [styles.alignBottom]: !authenticated,
            })}
          >
            <StarRating
              className={styles.rating}
              rating={company?.kununuData?.ratingAverage}
            />
            <Meta size="xsmall" noMargin data-testid="kununu-ratings-count">
              <FormattedMessage
                id="CP_KUNUNU_REVIEWS_NUMBER"
                values={{ number: company?.kununuData?.ratingCount || 0 }}
              />
            </Meta>
          </div>
        )}
        {customContent}
        {showButtons && (
          <div className={styles.buttonPanel}>
            {buttons.map((button) => button)}
          </div>
        )}
        {publicationStatus === 'CREATED' && !mobileProtocol && (
          <div className={styles.buttonPanel}>
            <Button
              className={styles.brandmanagerButton}
              size="small"
              variant="secondary"
              onClick={() => window.open(urlWithReleaseProtocol)}
            >
              <FormattedMessage id="CP_DRAFT_COMPANY_BUTTON_FINISH" />
            </Button>
          </div>
        )}
      </Card>
    </>
  );
};

export { CompanyCard };
